/**
 * EventCategories — the activities an event is made of.
 *
 * An activity carries either a route (point to point, with a distance) or a
 * venue (a fixed place), and each gets its own fact row. Both are conditional,
 * so an activity with neither renders its name and description alone rather
 * than an empty table.
 *
 * Everything added to the route row is added INSIDE the cell it belongs to —
 * the GPX file under the distance it describes, each map above the address it
 * pins. A map in a row of its own would read as a third fact rather than as
 * the picture of the second.
 */

import React from 'react';
import EventPointMap from './EventPointMap';
import EventRouteLine from './EventRouteLine';
import EventRouteMap from './EventRouteMap';
import useGpxRoute from './useGpxRoute';
import parsePoint from './geoPoint';
import parseLine from './geoLine';
import { ProseParagraph, hasProse } from './EventProseSection';

/**
 * What an activity IS — Run, Swim, Ride.
 *
 * The row stores `activity_type_id`, and an id is not something to show a
 * visitor. The name is used when the public data source sends the joined row
 * (as `activity_type`) and nothing is shown when it does not — a bare "1" would
 * be worse than silence.
 */
function activityTypeName(activity) {
  const type = activity?.activity_type;
  if (!type) return '';
  if (typeof type === 'string') return type;
  return type.name || type.label || type.longcode || '';
}

/**
 * The unit a distance was measured in.
 *
 * "km" was hard-coded here, which is a guess — the column is
 * `distance_measuring_unit_id`, and an event measured in miles was being
 * labelled in kilometres. The joined row is used when it is sent; km remains
 * the fallback, because it is what every event in this application uses today.
 */
function distanceUnit(route) {
  const unit = route?.distance_measuring_unit || route?.distance_unit;
  if (!unit) return 'km';
  if (typeof unit === 'string') return unit;
  return unit.shortcode || unit.symbol || unit.name || 'km';
}

/**
 * What it costs to enter this activity, and how many can.
 *
 * Both moved from the event's registration row onto the activity, so they are
 * stated per activity — which is also the only place they can be stated
 * truthfully once an event runs several at different prices.
 */
function ActivityTerms({ activity, formatFee, formatNumber }) {
  const fee = activity?.fee_amount;
  const places = activity?.max_participants;
  const hasFee = fee !== null && fee !== undefined && fee !== '';
  const hasPlaces = places !== null && places !== undefined && places !== '';
  if (!hasFee && !hasPlaces) return null;

  return (
    // Two facts, so a two-column row — on the route's three-column grid the
    // pair sat lopsided, one narrow cell beside one that took the rest.
    <div className="evb-cat__facts evb-cat__facts--pair">
      {hasFee && (
        <div>
          <span>Entry fee</span>
          <b>{Number(fee) > 0 ? formatFee(fee) : 'Free'}</b>
        </div>
      )}
      {hasPlaces && (
        <div><span>Places</span><b className="evb-num">{formatNumber(places)}</b></div>
      )}
    </div>
  );
}

function CategoryCard({ activity, formatDistance, formatFee, formatNumber, resolveUrl }) {
  const route = activity?.route;
  const venue = activity?.venue;

  // The GPX file is no longer offered as a download — it is read to DRAW the
  // course below, which is what a visitor actually wants from it. The URL is
  // still put through resolveUrl, because it is fetched: an unsafe value must
  // not reach the network any more than it should have reached an href.
  const gpxUrl = route ? resolveUrl(route.gpx_file_url) : null;
  const startPoint = route ? parsePoint(route.start_point) : null;
  const endPoint = route ? parsePoint(route.end_point) : null;
  const venuePoint = venue ? parsePoint(venue.venue_point) : null;
  // The stored line if there is one, otherwise the organiser's GPX file, which
  // is the most accurate description of the course anyone has.
  const storedLine = route ? parseLine(route.route_polyline) : null;
  const gpxLine = useGpxRoute(!storedLine && gpxUrl ? gpxUrl : '');
  const courseLine = storedLine || gpxLine;
  const typeName = activityTypeName(activity);

  return (
    <article className="evb-cat">
      <div className="evb-cat__hd">
        <h3>{activity?.name}</h3>
        <div className="evb-cat__tags">
          {typeName && <span className="evb-tag evb-tag--type">{typeName}</span>}
          {route && <span className="evb-tag">Route</span>}
          {venue && <span className="evb-tag">Venue</span>}
        </div>
      </div>
      {/* {activity?.description && <p className="evb-cat__d">{activity.description}</p>} */}
      {/* {activity?.description && (
        <ProseParagraph text={activity.description} className="evb-cat__d" lines={8} />
      )} */}
      {/* `hasProse` rather than a truthiness check: an activity whose
          description was formatted and then cleared holds `<p></p>`, which is
          not content and must not open a padded empty block under the header. */}
      {hasProse(activity?.description) && (
        <ProseParagraph rich text={activity.description} className="evb-cat__d" lines={8} />
      )}

      {route && (
        <div className={`evb-cat__facts${courseLine ? ' evb-cat__facts--course' : ''}`}>
          <div>
            <span>Distance</span>
            <b className="evb-num">{formatDistance(route.total_distance)} {distanceUnit(route)}</b>
          </div>
          <div>
            <span>Starts at</span>
            <EventPointMap
              point={startPoint}
              label={route.start_address}
              title={`Start of ${activity?.name || 'the route'}`}
            />
            <b>{route.start_address}</b>
          </div>
          <div>
            <span>Finishes at</span>
            <EventPointMap
              point={endPoint}
              label={route.end_address}
              title={`Finish of ${activity?.name || 'the route'}`}
            />
            <b>{route.end_address}</b>
          </div>

          {/* Full width, across both columns: a route is a shape, and a shape
              needs the room. */}
          {courseLine && (
            <EventRouteMap
              line={courseLine}
              startLabel={route.start_address}
              endLabel={route.end_address}
              title={`${activity?.name || 'Activity'} — the course`}
            />
          )}
        </div>
      )}

      {venue && (
        <div className="evb-cat__facts evb-cat__facts--pair">
          <div><span>Venue</span><b>{venue.venue_name}</b></div>
          <div>
            <span>Address</span>
            <EventPointMap
              point={venuePoint}
              label={venue.venue_address}
              title={`Venue for ${activity?.name || 'this activity'}`}
            />
            <b>{venue.venue_address}</b>
          </div>
        </div>
      )}

      <ActivityTerms
        activity={activity}
        formatFee={formatFee}
        formatNumber={formatNumber}
      />

      {/* The stops in the order they are met, between the start and the
          finish they sit between — see EventRouteLine. */}
      <EventRouteLine
        stops={activity?.pit_stops}
        startAddress={route?.start_address}
        endAddress={route?.end_address}
      />
    </article>
  );
}

export default function EventCategories({
  categories, formatDistance, formatFee, formatNumber, resolveUrl,
}) {
  const items = Array.isArray(categories) ? categories : [];

  return (
    <section className="evb-sec">
      <h2 className="evb-h">Event activities</h2>
      <p className="evb-note">Choose one when you register.</p>

      {items.length > 0 ? (
        <div className="evb-cats">
          {items.map((activity, index) => (
            <CategoryCard
              key={activity?.id ?? index}
              activity={activity}
              formatDistance={formatDistance}
              formatFee={formatFee}
              formatNumber={formatNumber}
              resolveUrl={resolveUrl}
            />
          ))}
        </div>
      ) : (
        // An empty list says so rather than leaving a heading over blank space.
        <p className="evb-note">No activities published yet.</p>
      )}
    </section>
  );
}
